import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSubmitted(true);
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 
              className="text-2xl font-medium text-stone-800 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Check Your Email
            </h1>
            
            <p className="text-stone-600 mb-6">
              If an account with <span className="font-medium">{email}</span> exists, 
              we've sent a password reset link. Please check your inbox and spam folder.
            </p>
            
            <p className="text-sm text-stone-500 mb-8">
              The link will expire in 1 hour.
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="w-full rounded-full"
                data-testid="try-different-email"
              >
                Try a different email
              </Button>
              
              <Link to="/login">
                <Button
                  variant="ghost"
                  className="w-full rounded-full text-stone-600"
                  data-testid="back-to-login"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-stone-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-amber-600" />
            </div>
            
            <h1 
              className="text-2xl font-medium text-stone-800 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Forgot Password?
            </h1>
            
            <p className="text-stone-600">
              No worries. Enter your email and we'll send you a link to reset your password.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-stone-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-2 rounded-xl"
                required
                data-testid="forgot-password-email"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full text-[#1A1A2E] py-6"
              style={{ backgroundColor: '#C9A84C' }}
              data-testid="send-reset-link"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
          
          <div className="mt-8 text-center">
            <Link 
              to="/login" 
              className="text-stone-600 hover:text-stone-800 text-sm flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
